import {Colors} from "@bounce/shared/ui";
import {PaymentPlanFrequency, PlanRating} from "@bounce/shared/enums";
import {Dayjs} from "dayjs";
import {nextFirstAndFifteenthDates} from "../../../../../../account/payment-plans/utils";
import {PaymentPlanOffer, IPlanMatrix} from "../../../../../../../declerations/server/payment_plan_models";
import {divide, multiply} from "@bounce/shared/helpers";
import {PLAN_STEPS} from "../../utils";

enum PlanType {
  MATRIX_NORMAL = "MATRIX_NORMAL",
  MATRIX_DOWN_PAYMENT = "MATRIX_DOWN_PAYMENT",
  MATRIX_HARDSHIP = "MATRIX_HARDSHIP",
}

type HardshipAmountType = number | "custom";

const DEFAULT_VALUES = {
  downPaymentInstallment: 25,
  hardshipInstallment: 75,
  numberOfHardshipInstallments: 3,
};

const PLAN_BUILDER_STEPS = [PLAN_STEPS.CHOOSE_PLAN, PLAN_STEPS.PAYMENT, PLAN_STEPS.AUTHORIZATION];

type PaymentPlanBuilderFrequency = "weekly" | "bi_weekly" | "monthly" | "first_and_fifteenth";
interface IGetFirstPlan {
  planType?: PlanType;
  downPayment?: number;
  numberOfHardshipInstallments?: number;
  hardshipAmount?: HardshipAmountType;
  hardshipCustomAmount?: number;
}

const getFirstPlan = ({
  planType = PlanType.MATRIX_NORMAL,
  downPayment,
  numberOfHardshipInstallments,
  hardshipAmount,
  hardshipCustomAmount,
}: IGetFirstPlan) => {
  if (planType === PlanType.MATRIX_HARDSHIP && hardshipAmount && numberOfHardshipInstallments) {
    const hardshipInstallmentSize =
      hardshipAmount === "custom" && hardshipCustomAmount ? hardshipCustomAmount : Number(hardshipAmount);
    return {
      number_of_installments: numberOfHardshipInstallments,
      installment_size: multiply(hardshipInstallmentSize, 100),
    };
  }
  if (planType === PlanType.MATRIX_DOWN_PAYMENT && downPayment) {
    return {number_of_installments: 1, installment_size: multiply(downPayment, 100)};
  }
  return {number_of_installments: 0, installment_size: 0};
};

const ratingColorMap = {
  [PlanRating.GREEN]: Colors.green._800,
  [PlanRating.YELLOW]: Colors.orange._700,
  [PlanRating.RED]: Colors.red._800,
};

const getRankStyles = (rank: PlanRating, isSelected: boolean) =>
  ({
    [PlanRating.GREEN]: {
      color: isSelected ? Colors.bw.white : Colors.green._900,
      backgroundColor: isSelected ? Colors.green._900 : Colors.green._100,
      divider: Colors.green._200,
    },
    [PlanRating.YELLOW]: {
      color: isSelected ? Colors.bw.white : Colors.orange._800,
      backgroundColor: isSelected ? Colors.orange._700 : Colors.orange._100,
      divider: Colors.orange._200,
    },
    [PlanRating.RED]: {
      color: isSelected ? Colors.bw.white : Colors.red._900,
      backgroundColor: isSelected ? Colors.red._800 : Colors.red._100,
      divider: Colors.red._200,
    },
  }[rank] || {
    color: Colors.red._100,
    backgroundColor: Colors.red._100,
    divider: Colors.green._200,
  });

const planTypeToInstallments: Record<string, number> = {
  [PlanType.MATRIX_NORMAL]: 0,
  [PlanType.MATRIX_DOWN_PAYMENT]: 1,
  [PlanType.MATRIX_HARDSHIP]: 3,
};

const getLastDate = (lastDate: Dayjs, currentFrequency: PaymentPlanFrequency): Dayjs => {
  if (currentFrequency) {
    if (currentFrequency === "weekly") {
      return lastDate.add(1, "week");
    }
    if (currentFrequency === "bi_weekly") {
      return lastDate.add(2, "week");
    }
    if (currentFrequency === "first_and_fifteenth") {
      const startDate = lastDate.add(1, "day");
      const nextFirstAndFifteenth = nextFirstAndFifteenthDates({numberOfDays: 30, startDate: startDate});
      return nextFirstAndFifteenth[0];
    }
    if (currentFrequency === "monthly") {
      return lastDate.add(1, "month");
    }
  }
  return lastDate.add(1, "day");
};

const convertPlanMatrixToPaymentPlanOffer = ({
  planMatrix,
  accountId,
  totalBalance,
  endDate,
}: {
  planMatrix: IPlanMatrix;
  accountId: string;
  totalBalance: number;
  endDate?: string;
}): PaymentPlanOffer => {
  return {
    account_id: accountId,
    installment_frequency: planMatrix.installment_frequency,
    number_of_installments: planMatrix.number_of_installments,
    installment_size: planMatrix.installment_size,
    discount_fraction: planMatrix.discount_fraction,
    total_amount: totalBalance,
    amount_before_discount_cents: 0,
    is_recommended: false,
    abstract_payment_plan_name: planMatrix.abstract_payment_plan_name,
    start_date: planMatrix.start_date,
    end_date: endDate || planMatrix.start_date,
    resolution_expert_only: true,
  };
};

const TableCellStyle = {
  height: "40px",
  justifyContent: "center",
};

const getError = (event: any, totalBalance: number, setError: (event: any) => void) => {
  const totalBalanceDollar = divide(totalBalance, 100);
  if (event.target.value < 25) {
    setError("Must be min 25$");
  } else if (event.target.value > totalBalanceDollar) {
    setError("Exceeded the total balance");
  } else {
    setError("");
  }
};

export {
  getFirstPlan,
  ratingColorMap,
  PlanRating,
  getRankStyles,
  getLastDate,
  planTypeToInstallments,
  convertPlanMatrixToPaymentPlanOffer,
  PlanType,
  TableCellStyle,
  PLAN_BUILDER_STEPS,
  getError,
  DEFAULT_VALUES,
};
export type {HardshipAmountType, IGetFirstPlan, PaymentPlanBuilderFrequency};
